import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { generatePreservationPDF, generateGenericDoc } from '../utils/generatePdf';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import timezone from 'dayjs/plugin/timezone';
import moment from 'moment-timezone';

const CARRIER_KEYS = ['tmobile', 'att', 'vzw', 'dish'];

function PreservationTool() {
	// The Provider Directory links here with ?carrier=<key>&item=<number>.
	const [params] = useSearchParams();
	const [preservationTextArray, setPreservationTextArray] = useState([]);
	const [itemToPreserve, setItemToPreserve] = useState(params.get('item') || "");
	const [preservationType, setPreservationType] = useState('carrier');
	const [carrierAlignment, setCarrierAlignment] = useState(CARRIER_KEYS.includes(params.get('carrier')) ? params.get('carrier') : 'choose_carrier');
	const [caseNumber, setCaseNumber] = useState("");
	const [name, setName] = useState("");
	const [title, setTitle] = useState("");
	const [dateTimeOffset, setDateTimeOffest] = useState("");
	const [areaSearchRadius, setAreaSearchRadius] = useState(".25 miles");
	const [telephoneNumber, setTelephoneNumber] = useState("");
	const [email, setEmail] = useState("");
	const [preservationDate, setPreservationDate] = useState("");
	const [datePickerTZ, setDatePickerTZ] = React.useState('UTC');
	const [timeZone, setTimeZone] = React.useState('America/New_York');
	const timeZones = moment.tz.names();
	const [secretCode, setSecretCode] = useState("");

	const handleChange = (event, newAlignment) => {
		console.log("Preservation Type Changed: ", newAlignment);
		setPreservationType(newAlignment);
	};
	const typeOfPreservation = { value: preservationType, onChange: handleChange, exclusive: true };

	const handleCarrierChange = (event, newAlignment) => { setCarrierAlignment(newAlignment); };
	const carrierControl = { value: carrierAlignment, onChange: handleCarrierChange, exclusive: true };

	if (name && title === "") {
		var directory = {
			"Matthew Tunney": { title: "Task Force Officer", email: "mtunney@fbi.gov", telephoneNumber: "914-280-3671" },
			"Philippe Goulet": { title: "Special Agent", email: "ptgoulet@fbi.gov", telephoneNumber: "845-570-7418" },
			"Kyle Gorman": { title: "Special Agent", email: "kpgorman2@fbi.gov", telephoneNumber: "703-722-4830" },
			"Justin Gray": { title: "Special Agent", email: "jmgray3@fbi.gov", telephoneNumber: "646-341-0487" },
		};
		if (directory[name]) {
			setTitle(directory[name].title);
			setEmail(directory[name].email);
			setTelephoneNumber(directory[name].telephoneNumber);
			setSecretCode("c26");
		}
	}

	const addToPreservation = () => {
		if (itemToPreserve.trim() === "") return;
		if (preservationType === "area_search") {
			setPreservationTextArray((previousArray) => {
				let adjustedDateLow = dayjs(preservationDate).subtract(dateTimeOffset, 'minute');
				adjustedDateLow = dayjs(adjustedDateLow).format('MM/DD/YY hh:mm A UTC');
				let adjustedDateHigh = dayjs(preservationDate).add(dateTimeOffset, 'minute');
				adjustedDateHigh = dayjs(adjustedDateHigh).format('MM/DD/YY hh:mm A UTC');
				var preservationStringWithDate = itemToPreserve + " from " + adjustedDateLow + " to " + adjustedDateHigh + " within " + areaSearchRadius;
				return [...previousArray, preservationStringWithDate];
			});
		} else {
			setPreservationTextArray((previousArray) => {
				const formattedDate = preservationDate ? dayjs(preservationDate).format('MM/DD/YY') : '';
				var preservationStringWithDate = itemToPreserve + " from " + formattedDate + " at 12:00 AM UTC to present";
				return [...previousArray, preservationStringWithDate];
			});
			setItemToPreserve("");
		}
	};

	const useSecretMode = secretCode === "c26";
	const generateDoc = useSecretMode ? generatePreservationPDF : generateGenericDoc;

	const generatePreservationRequest = function() {
    trackToolAction('/preservation', 'Preservation Tool');
		const preservationRequestComponents = {
			caseNumber, name, title, email, telephoneNumber, preservationDate,
			preservationTextArray, preservationType, carrierAlignment, secretCode,
			areaSearchRadius,
			currentDate: dayjs().format("MM-DD-YY"),
			carrierContent: {},
			returnAddress: ["New York Division", "Westchester Resident Agency", "600 Midland Ave,", "Rye, NY 10580"],
			pointOfContact: "The point of contact for this matter is " + name + ", telephone number " + telephoneNumber + ", " + email,
		};

		if (preservationType === "area_search") {
			var letterText = ["The below listed cell tower(s) are related to an ongoing Federal Bureau of Investigation (FBI), United States Department of Justice, criminal investigation. It is requested pursuant to 18 USC 2703(f) that records associated with said identifiers be preserved pending the issuance of a search warrant or other legal process seeking disclosure of such information:", "All phone records, timing advance data, cell sites, subscriber information, location data, and any other information related to all cell towers that service the following area(s) for the below dates and times.", "The FBI will obtain the appropriate legal authority for disclosure of this information and forward this material to your attention within ninety (90) days.", "You are requested not to disclose the existence of this request to the subscriber or any other person, other than as necessary to comply with this request. Any such disclosure could subject you to criminal liability for obstruction of justice."];
		} else {
			var letterText = ["The below listed identifer(s) are related to an ongoing Federal Bureau of Investigation (FBI), United States Department of Justice, criminal investigation. It is requested pursuant to 18 USC 2703(f) that records associated with said identifiers be preserved pending the issuance of a search warrant or other legal process seeking disclosure of such information:", "All phone records, timing advance data, cell sites, subscriber information, location data, and any other information related to the below identifiers and time frames.", "The FBI will obtain the appropriate legal authority for disclosure of this information and forward this material to your attention within ninety (90) days.", "You are requested not to disclose the existence of this request to the subscriber or any other person, other than as necessary to comply with this request. Any such disclosure could subject you to criminal liability for obstruction of justice."];
		}

		if (!useSecretMode) {
			letterText[0] = "The below listed identifer(s) are related to an ongoing investigation. It is requested that records associated with said identifiers be preserved pending the issuance of a search warrant or other legal process seeking disclosure of such information:";
			letterText[2] = "This agency will obtain the appropriate legal authority for disclosure of this information and forward this material to your attention within ninety (90) days.";
		}

		var carrierSpecificLanguage = {
			tmobile: { address: ["T-Mobile", "4 Sylvan Way", "Parsnippany, NJ 07054"], letterText },
			vzw: { address: ["Verizon Attn: VSAT", "VSAT.CCT@one.verizon.com"], letterText },
			att: { address: ["AT&T Global Legal Demands", "gldc@att.com"], letterText },
			dish: { address: ["Subsentio", "rp-dish@subsentio.com"], letterText },
		};

		if (preservationType === "area_search") {
			preservationRequestComponents.carrierAlignment = "tmobile";
			preservationRequestComponents.carrierContent.language = carrierSpecificLanguage.tmobile.letterText;
			preservationRequestComponents.carrierContent.address = carrierSpecificLanguage.tmobile.address;
			generateDoc(preservationRequestComponents);

			setTimeout(function() {
				preservationRequestComponents.carrierAlignment = "att";
				preservationRequestComponents.carrierContent.language = carrierSpecificLanguage.att.letterText;
				preservationRequestComponents.carrierContent.address = carrierSpecificLanguage.att.address;
				generateDoc(preservationRequestComponents);

				setTimeout(function() {
					const modifiedPreservationTextArray = preservationTextArray.map(item => {
						const index = item.indexOf("within");
						if (index !== -1) return item.substring(0, index).trim();
						return item;
					});
					preservationRequestComponents.preservationTextArray = modifiedPreservationTextArray;
					preservationRequestComponents.carrierAlignment = "vzw";
					preservationRequestComponents.carrierContent.language = carrierSpecificLanguage.vzw.letterText;
					preservationRequestComponents.carrierContent.address = carrierSpecificLanguage.vzw.address;
					generateDoc(preservationRequestComponents);
					resetForm();
				}, 1000);
			}, 1000);
		} else {
			preservationRequestComponents.carrierContent.language = carrierSpecificLanguage[carrierAlignment].letterText;
			preservationRequestComponents.carrierContent.address = carrierSpecificLanguage[carrierAlignment].address;
			generateDoc(preservationRequestComponents);
			resetForm();
		}
	}

	const usersTimeChanged = function(e) {
		if (preservationType === "area_search") {
			const localDateStr = dayjs(e).format('YYYY-MM-DDTHH:mm:ss');
			const utcDate = dayjs.tz(localDateStr, timeZone).utc();
			setPreservationDate(utcDate);
		} else {
			setPreservationDate(e);
		}
	}

	const doUpdateTimezone = function(e) { setTimeZone(e.target.value); }

	const resetForm = function() {
		setItemToPreserve("");
		setPreservationTextArray([]);
		setPreservationType('carrier');
		setCarrierAlignment('choose_carrier');
		setPreservationDate("");
		setDatePickerTZ('UTC');
		setTimeZone('America/New_York');
	}

	const sortedTimeZones = timeZones.sort((a, b) => {
		if (a.startsWith('US/')) return -1;
		if (b.startsWith('US/')) return 1;
		if (a === 'America/New_York') return -1;
		if (b === 'America/New_York') return 1;
		if (a.includes("America/") && !b.includes("America/")) return -1;
		if (!a.includes("America/") && b.includes("America/")) return 1;
		return a.localeCompare(b);
	});

	const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px' } };

	return (
		<>
			<Navbar pageTitle="Create Preservation Request" />
			<Container maxWidth="md" sx={{ py: 4 }}>

				<Box sx={{ mb: 3 }}>
					<Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
						Preservation Request
					</Typography>
					<Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
						Create a preservation request for phone records or area searches.
					</Typography>
				</Box>

				{/* Agent Info */}
				<Paper elevation={0} sx={{
					borderRadius: '14px',
					boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
					p: '26px 26px 22px', mb: 2.5,
				}}>
					<Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1 }}>
						Your Information
					</Typography>
					<TextField label="Case Number" variant="outlined" fullWidth margin="normal" value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} sx={inputSx} />
					<TextField label="Your Name" variant="outlined" fullWidth margin="normal" value={name} onChange={(e) => setName(e.target.value)} sx={inputSx} />
					<TextField label="Your Title" variant="outlined" fullWidth margin="normal" value={title} onChange={(e) => setTitle(e.target.value)} sx={inputSx} />
					<TextField label="Your Email" variant="outlined" fullWidth margin="normal" value={email} onChange={(e) => setEmail(e.target.value)} sx={inputSx} />
					<TextField label="Your Telephone Number" variant="outlined" fullWidth margin="normal" value={telephoneNumber} onChange={(e) => setTelephoneNumber(e.target.value)} sx={inputSx} />
					<TextField label="Secret Code (optional)" variant="outlined" fullWidth margin="normal" value={secretCode} onChange={(e) => setSecretCode(e.target.value)} sx={inputSx} />
				</Paper>

				{/* Preservation Details */}
				<Paper elevation={0} sx={{
					borderRadius: '14px',
					boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
					p: '26px 26px 22px', mb: 2.5,
				}}>
					<Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 2 }}>
						Preservation Details
					</Typography>

					<Typography sx={{ fontSize: '13px', color: '#374151', mb: 1 }}>Type of preservation</Typography>
					<ToggleButtonGroup size="large" {...typeOfPreservation} aria-label="Preservation Type" fullWidth sx={{ mb: 2.5 }}>
						<ToggleButton value="carrier" key="carrier">Specific Carrier</ToggleButton>
						<ToggleButton value="area_search" key="area_search">Area Search</ToggleButton>
					</ToggleButtonGroup>

					{preservationType !== "area_search" && (
						<>
							<ToggleButtonGroup size="large" {...carrierControl} aria-label="Carrier" fullWidth sx={{ mb: 2.5 }}>
								<ToggleButton value="tmobile" key="tmobile">T-Mobile</ToggleButton>
								<ToggleButton value="att" key="att">AT&amp;T</ToggleButton>
								<ToggleButton value="vzw" key="vzw">Verizon</ToggleButton>
								<ToggleButton value="dish" key="dish">Dish</ToggleButton>
							</ToggleButtonGroup>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DemoContainer components={['DatePicker']}>
									<DatePicker timezone={datePickerTZ} label="Start date of request (UTC)" onChange={usersTimeChanged} />
								</DemoContainer>
							</LocalizationProvider>
							<TextField
								label="Item to Preserve (Phone Number, IMSI, IMEI, etc)"
								variant="outlined" fullWidth margin="normal"
								value={itemToPreserve}
								onChange={(e) => setItemToPreserve(e.target.value)}
								sx={inputSx}
							/>
						</>
					)}

					{preservationType === "area_search" && (
						<>
							<Typography sx={{ fontSize: '13px', color: '#374151', mb: 1 }}>Timezone</Typography>
							<Select value={timeZone} fullWidth onChange={doUpdateTimezone} sx={{ mb: 2, borderRadius: '8px' }}>
								{sortedTimeZones.map((tz, index) => (
									<MenuItem key={index} value={tz}>{tz}</MenuItem>
								))}
							</Select>
							<TextField
								label="Latitude and Longitude (e.g. 40.7128, -74.0060)"
								variant="outlined" fullWidth margin="normal"
								value={itemToPreserve}
								onChange={(e) => setItemToPreserve(e.target.value)}
								sx={inputSx}
							/>
							<TextField
								label="Radius (AT&T and T-Mobile only) (e.g. 0.25 miles)"
								variant="outlined" fullWidth margin="normal"
								value={areaSearchRadius}
								onChange={(e) => setAreaSearchRadius(e.target.value)}
								sx={inputSx}
							/>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DemoContainer components={['DateTimePicker']}>
									<DateTimePicker timezone={datePickerTZ} label="Date and time in local time" onChange={usersTimeChanged} />
								</DemoContainer>
							</LocalizationProvider>
							<TextField
								label="Plus/Minus Time in Minutes (e.g. 30)"
								variant="outlined" fullWidth margin="normal"
								value={dateTimeOffset}
								onChange={(e) => setDateTimeOffest(e.target.value)}
								sx={inputSx}
							/>
						</>
					)}

					<Button
						fullWidth variant="contained"
						onClick={addToPreservation}
						sx={{
							mt: 1.5, p: '10px 20px', bgcolor: '#6d28d9',
							fontSize: '14px', fontWeight: 600, borderRadius: '8px',
							'&:hover': { bgcolor: '#5b21b6' },
						}}
					>
						Add to Preservation Request
					</Button>
				</Paper>

				{/* Items list + Generate */}
				{preservationTextArray.length > 0 && (
					<Paper elevation={0} sx={{
						borderRadius: '14px',
						boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)',
						p: '26px 26px 22px', mb: 2.5,
					}}>
						<Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
							Items to Preserve
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
							{preservationTextArray.map((item, idx) => (
								<Box key={idx} sx={{
									p: '10px 14px', bgcolor: '#f8fafc',
									border: '1px solid #e2e8f0', borderRadius: '8px',
								}}>
									<Typography sx={{ fontSize: '13px', color: '#374151' }}>{item}</Typography>
								</Box>
							))}
						</Box>
						<Button
							fullWidth variant="contained"
							onClick={generatePreservationRequest}
							sx={{
								p: '10px 20px', bgcolor: '#4c1d95',
								fontSize: '14px', fontWeight: 600, borderRadius: '8px',
								'&:hover': { bgcolor: '#3b1378' },
							}}
						>
							Generate Preservation Request
						</Button>
					</Paper>
				)}
			</Container>
		</>
	);
}

export default PreservationTool;
